import React, { Suspense, lazy } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import WhatsAppButton from './components/WhatsAppButton';
import ScrollToTop from './components/ScrollToTop';
import LoadingScreen from './components/LoadingScreen';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/admin/ProtectedRoute';
import ErrorBoundary from './components/admin/ErrorBoundary';
import { taxInvoiceConfig, cashInvoiceConfig } from './lib/invoiceConfig';

// Public website pages
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Services = lazy(() => import('./pages/Services'));
const Fleet = lazy(() => import('./pages/Fleet'));
const Projects = lazy(() => import('./pages/Projects'));
const Contact = lazy(() => import('./pages/Contact'));

// Auth pages
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));

// Quotation Manager pages
const QmDashboard = lazy(() => import('./pages/Launch'));
const SavedQuotations = lazy(() => import('./pages/SavedQuotations'));
const QuotationEditor = lazy(() => import('./pages/QuotationEditor'));
const SavedInvoices = lazy(() => import('./pages/SavedInvoices'));
const InvoiceEditor = lazy(() => import('./pages/InvoiceEditor'));

const PublicLayout = ({ children }) => (
  <>
    <Header />
    <main>{children}</main>
    <Footer />
    <WhatsAppButton />
  </>
);

const Protect = ({ children }) => (
  <ProtectedRoute>
    <ErrorBoundary>{children}</ErrorBoundary>
  </ProtectedRoute>
);

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<LoadingScreen />}>
            <Routes>
              {/* Public website routes */}
              <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
              <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
              <Route path="/services" element={<PublicLayout><Services /></PublicLayout>} />
              <Route path="/fleet" element={<PublicLayout><Fleet /></PublicLayout>} />
              <Route path="/projects" element={<PublicLayout><Projects /></PublicLayout>} />
              <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />

              {/* Auth */}
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/admin/login" element={<Navigate to="/login" replace />} />

              {/* Quotation Manager (protected) */}
              <Route path="/admin" element={<Protect><QmDashboard /></Protect>} />
              <Route path="/admin/quotations" element={<Protect><SavedQuotations /></Protect>} />
              <Route path="/admin/quotations/new" element={<Protect><QuotationEditor /></Protect>} />
              <Route path="/admin/quotations/:id" element={<Protect><QuotationEditor /></Protect>} />
              <Route path="/admin/tax-invoices" element={<Protect><SavedInvoices config={taxInvoiceConfig} /></Protect>} />
              <Route path="/admin/tax-invoices/new" element={<Protect><InvoiceEditor config={taxInvoiceConfig} /></Protect>} />
              <Route path="/admin/tax-invoices/:id" element={<Protect><InvoiceEditor config={taxInvoiceConfig} /></Protect>} />
              <Route path="/admin/cash-invoices" element={<Protect><SavedInvoices config={cashInvoiceConfig} /></Protect>} />
              <Route path="/admin/cash-invoices/new" element={<Protect><InvoiceEditor config={cashInvoiceConfig} /></Protect>} />
              <Route path="/admin/cash-invoices/:id" element={<Protect><InvoiceEditor config={cashInvoiceConfig} /></Protect>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
